import { Link2, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ConfirmDelete } from '@/components/ConfirmDelete';
import { toast } from '@/hooks/use-toast';
import { useDeleteWebhooks } from '@/hooksApi';
import type { WebHooksDataResponse } from '@/types';

interface WebhookRowProps {
  webhook: WebHooksDataResponse;
  onEdit: (webhook: WebHooksDataResponse) => void;
}

export const WebhookRow = ({ webhook, onEdit }: WebhookRowProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutate: deleteWebhook, isLoading: isDeleting } = useDeleteWebhooks();

  const handleDelete = () => {
    if (isDeleting) return;
    deleteWebhook(webhook.uid, {
      onSuccess: () => {
        queryClient.invalidateQueries(['webhooks']);
        toast({ title: t('webhook_deleted') });
      },
    });
  };

  return (
    <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm'>
      <div className='p-5 space-y-3'>
        {/* Header */}
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-3 min-w-0'>
            <div className='h-9 w-9 rounded-lg bg-sd-muted flex items-center justify-center shrink-0'>
              <Link2 className='h-4 w-4 text-sd-muted-foreground' />
            </div>
            <span className='text-sm font-semibold text-sd-foreground truncate'>{webhook.url}</span>
          </div>
          <div className='flex items-center gap-1 shrink-0'>
            <button
              onClick={() => onEdit(webhook)}
              className='p-1.5 rounded-lg text-sd-muted-foreground hover:text-sd-foreground hover:bg-sd-muted transition-colors'
            >
              <Pencil className='h-4 w-4' />
            </button>
            <ConfirmDelete
              title={t('delete_webhook')}
              description={t('webhook_will_be_deleted')}
              onConfirm={handleDelete}
              disabled={isDeleting}
            />
          </div>
        </div>

        {/* Triggers */}
        {webhook.events && webhook.events.length > 0 && (
          <div className='space-y-1'>
            <p className='text-[11px] font-medium text-sd-muted-foreground uppercase tracking-wider'>
              {t('triggers')}
            </p>
            <div className='flex flex-wrap gap-1.5'>
              {webhook.events.map((event) => (
                <span
                  key={event.uid}
                  className='px-2 py-0.5 rounded-md bg-sd-muted text-xs font-medium text-sd-muted-foreground'
                >
                  {event.title}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Headers */}
        {webhook.properties && webhook.properties.length > 0 && (
          <div className='space-y-1'>
            <p className='text-[11px] font-medium text-sd-muted-foreground uppercase tracking-wider'>
              {t('headers')}
            </p>
            <div className='space-y-1'>
              {webhook.properties.map((h) => (
                <div key={h.uid} className='flex items-center gap-2 text-xs font-mono'>
                  <span className='text-sd-foreground font-semibold'>{h.key}:</span>
                  <span className='text-sd-muted-foreground'>{h.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
